import { useEffect, useRef, useState } from "react";
import { useObservedElementRef } from "./useObservedElementRef";

/**
 * Observes an element's size and derives a value from it via `measure`. The
 * value is recomputed on mount (with no entry) and whenever the element
 * resizes (with the observed entry). Attach the returned `ref` to the element
 * to observe.
 *
 * The returned `ref` tracks element *identity*, not just the initial mount: a caller that swaps the
 * observed node — React re-creating it across a `<div>`/`<button>` change, or a composed callback ref
 * reassigning `ref.current` — gets the observer rebound onto the new node. Observing only whatever was
 * attached on mount would freeze the value at the old element's geometry, which for an overflow
 * measurement means content that can never be scrolled to.
 */
export const useResizeObserver = <V, T extends HTMLElement = HTMLDivElement>(
  measure: (element: T, entry?: ResizeObserverEntry) => V,
  initialValue: V
) => {
  const measureRef = useRef(measure);
  measureRef.current = measure;
  const [value, setValue] = useState<V>(initialValue);

  const { ref, element } = useObservedElementRef<T>();

  useEffect(() => {
    if (!element) return;

    const observer = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (!entry) return;
      setValue(measureRef.current(element, entry));
    });
    observer.observe(element);
    setValue(measureRef.current(element));

    return () => observer.disconnect();
  }, [element]);

  return { ref, value };
};
